import { useEffect, useRef, useState } from "react";

/**
 * Fade-and-lift a block in as it scrolls into view, and settle it back as it
 * leaves — so the Landmarks page reads as one place after another rather than a
 * static wall, which is how Blair's original site felt.
 *
 * Two deliberate choices:
 *
 *  - It reveals BOTH ways. Scrolling back up re-plays the entry, because the
 *    page is meant to feel alive in either direction. The element is never left
 *    invisible: `hidden` only ever means "not currently on screen".
 *
 *  - It triggers on ANY overlap with a shrunken viewport, never on a percentage
 *    OF THE ELEMENT. That distinction is the whole bug history of this page. An
 *    IntersectionObserver threshold is a fraction of the element's own area, so
 *    an element taller than the screen can never reach one: a 5,000px spread in
 *    a 900px window tops out at 0.18 and a threshold of 0.2 leaves it hidden
 *    forever, with no error anywhere. Three earlier fade attempts died on
 *    exactly this. `threshold: 0` plus a negative rootMargin gives the same
 *    "must be properly on screen" feel at every element height.
 *
 *  - It obeys `prefers-reduced-motion`. Plenty of this family are older readers,
 *    and motion sensitivity is real; when that setting is on, everything is
 *    simply visible with no animation at all. It also degrades to fully visible
 *    where IntersectionObserver is unavailable, so a block can never be stranded
 *    invisible by a failure.
 */
export default function useRevealOnScroll({ inset = 12 } = {}) {
  const ref = useRef(null);
  // Start visible. If anything below fails, the content still shows.
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || typeof IntersectionObserver === "undefined") return;

    // Only now is it safe to start hidden: the observer exists and will fire.
    setVisible(false);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setVisible(entry.isIntersecting));
      },
      { threshold: 0, rootMargin: `-${inset}% 0px -${inset}% 0px` }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [inset]);

  return [ref, visible];
}
